import { Injectable } from '@angular/core';
import { ColumnExport, ExportBuilder } from '@app/shared/export/index';
import { PortalDatePipe } from '@app/shared/pipes/portal-date.pipe';
import { Identifier } from '@app/utils/common';
import { StringMap } from '@app/utils/string-map';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { unparse } from 'papaparse';

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  constructor(
    private datePipe: PortalDatePipe,
    private translateService: TranslateService
  ) {}

  public createExport<T>(data: T[]): ExportBuilder<T> {
    return new ExportBuilder<T>(data, this);
  }

  /**
   * Export the given data as a csv representation and download it as a file with the given name.
   *
   * @param data Data to export.
   * @param columns Column definition (headers and value functions).
   * @param name i18n key, which is used as name of the file.
   * @param fileNameParameters
   */
  public export<T>(
    data: T[],
    columns: ColumnExport<T>[],
    name: string,
    fileNameParameters?: StringMap<string>
  ): void {
    const visibleColumns = columns.filter((column) => this.isIgnored(column) === false);

    const headers = this.calculateHeaders(visibleColumns);
    const values = this.calculateValues(data, visibleColumns);

    const csv = unparse([headers, ...values], {
      header: true,
    });

    this.saveAsFile(csv, name, fileNameParameters);
  }

  private calculateHeaders<T>(columns: ColumnExport<T>[]): string[] {
    return columns
      .map((column) => {
        let header: string[];

        if (typeof column.header === 'string') {
          header = [this.translateService.instant(column.header)];
        } else if (Array.isArray(column.header)) {
          header = column.header.map((header) => this.translateService.instant(header));
        } else {
          const result = column.header();

          if (Array.isArray(result)) {
            header = result;
          } else {
            header = [result];
          }
        }

        return header.map((_) => _.toUpperCase());
      })
      .reduce((a, b) => a.concat(b), []);
  }

  private calculateValues<T>(data: T[], columns: ColumnExport<T>[]): Identifier[][] {
    return data.map((entry) =>
      columns
        .map((column) => {
          let result: Identifier[];

          const transformedValue = column.transform(entry);

          if (Array.isArray(transformedValue)) {
            result = transformedValue;
          } else {
            result = [transformedValue];
          }

          return result;
        })
        .reduce((a, b) => a.concat(b), [])
    );
  }

  private saveAsFile(content: string, name: string, fileNameParameters: StringMap<string> = {}): void {
    // For some reason UTF-8 BOM is not added, cf. https://redmine.neotel.at/issues/40072
    // Current used version of FileSaver is v2.0.2
    // Looking at the code of FileSaver this should only work if (the browser) `navigator.msSaveOrOpenBlob` has that function.
    // Meaning, the BOM is added only when using some Microsoft Browser ... (why?)
    // Maybe this will be changed in the future of FileSaver.js; Until then we will set the BOM explicitely.

    const saveWithAutoBom = {
      autoBom: false,
      // autoBom: true
    };
    // Adding BOM explicitely (value `0xFEFF` will be automaticall set to `0xEFBBBF` by the browser)
    const blob = new Blob([String.fromCharCode(0xfeff), content], {
      type: 'text/csv;charset=utf-8',
    });

    const parameters = {
      ...fileNameParameters,
      date: this.datePipe.transform(new Date(), 'yyyy-MM-dd_HH-mm-ss'),
    };

    const fileName = this.translateService.instant(name, parameters);

    saveAs(blob, fileName, saveWithAutoBom);
  }

  private isIgnored<T>(column: ColumnExport<T>) {
    let ignore = false;

    if (typeof column.ignore === 'boolean') {
      ignore = column.ignore;
    } else if (typeof column.ignore === 'function') {
      ignore = column.ignore();
    }

    return ignore;
  }
}
